import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable } from 'rxjs';
import { baseUrl } from '../Data/service/global.service';
import {
  CreateInvoiceV2Dto,
  UpdateInvoiceV2Dto,
  FilterInvoicesV2Dto,
  InvoiceResponseDto,
  InvoiceListResponseDto,
  DocumentNumberStatsResponse,
  DocumentNumberPredictResponse,
  DocumentNumberGenerateResponse,
  CheckSequenceResponse,
  TaxAuthorityActionResponse,
  ReverseInvoiceResponse,
  InvoiceStatsResponse,
  UpdateAllocationNumberResponse,
  BulkConvertInvoicesResponse,
  MarkInvoicesMergedResponse,
  UpdateInvoicePaymentBody,
  UpdateInvoicePaymentResponse,
} from '../models/invoice-v2.model';

// Re-export types for convenience
export type {
  CreateInvoiceV2Dto,
  UpdateInvoiceV2Dto,
  FilterInvoicesV2Dto,
  InvoiceResponseDto,
  InvoiceListResponseDto,
};

@Injectable({
  providedIn: 'root',
})
export class InvoiceV2Service {
  private readonly baseEndpoint = 'invoices-v2';

  constructor(private http: HttpClient) {}

  /**
   * 1. Create Invoice
   * POST /invoices-v2
   */
  createInvoice(dto: CreateInvoiceV2Dto): Observable<InvoiceResponseDto> {
    return this.http.post<InvoiceResponseDto>(`${baseUrl}${this.baseEndpoint}`, dto);
  }

  /**
   * OTP: Get company average and threshold (average + 30%)
   * GET /invoices-v2/company/:companyId/average-amount
   * Compare invoiceAmount > threshold to decide if OTP is required
   */
  getCompanyAverageAmount(companyId: number): Observable<{ averageAmount: number; threshold: number }> {
    return this.http.get<{ averageAmount: number; threshold: number }>(
      `${baseUrl}${this.baseEndpoint}/company/${companyId}/average-amount`
    );
  }

  /**
   * OTP: Send OTP for suspicious invoice
   * POST /invoices-v2/otp/send
   */
  sendOtp(companyId: number, invoiceAmount: number): Observable<{
    success: boolean;
    message: string;
    expiresAt: string;
  }> {
    return this.http.post<{
      success: boolean;
      message: string;
      expiresAt: string;
    }>(`${baseUrl}${this.baseEndpoint}/otp/send`, {
      company_id: companyId,
      invoice_amount: invoiceAmount,
    });
  }

  /**
   * OTP: Verify OTP before creating invoice
   * POST /invoices-v2/otp/verify
   * Backend links OTP to invoice automatically after create (within 30 min).
   */
  verifyOtp(companyId: number, otp: number, invoiceId?: number): Observable<{
    success: boolean;
    verified: boolean;
    message: string;
    otpId?: number;
  }> {
    const body: { otp: number; company_id: number; invoice_id?: number } = {
      otp,
      company_id: companyId,
    };
    if (invoiceId != null) body.invoice_id = invoiceId;
    return this.http.post<{
      success: boolean;
      verified: boolean;
      message: string;
      otpId?: number;
    }>(`${baseUrl}${this.baseEndpoint}/otp/verify`, body);
  }

  /**
   * OTP: Link verified OTP to invoice (optional - backend usually does this automatically)
   * POST /invoices-v2/otp/link-invoice
   */
  linkOtpToInvoice(invoiceId: number, companyId: number): Observable<{
    success: boolean;
    linked: boolean;
    message: string;
  }> {
    return this.http.post<{
      success: boolean;
      linked: boolean;
      message: string;
    }>(`${baseUrl}${this.baseEndpoint}/otp/link-invoice`, {
      invoice_id: invoiceId,
      company_id: companyId,
    });
  }

  /**
   * PATCH /invoices-v2/:invoiceId/payment — update due_date / payment_status (including after confirm).
   */
  updateInvoicePayment(
    invoiceId: number,
    body: UpdateInvoicePaymentBody,
  ): Observable<UpdateInvoicePaymentResponse> {
    return this.http.patch<UpdateInvoicePaymentResponse>(
      `${baseUrl}${this.baseEndpoint}/${invoiceId}/payment`,
      body,
    );
  }

  /**
   * 2. Update Invoice
   * PUT /invoices-v2/:invoiceId
   */
  updateInvoice(invoiceId: number, dto: UpdateInvoiceV2Dto): Observable<InvoiceResponseDto> {
    return this.http.put<InvoiceResponseDto>(`${baseUrl}${this.baseEndpoint}/${invoiceId}`, dto);
  }

  /**
   * 3. Delete Invoice
   * DELETE /invoices-v2/:invoiceId
   */
  deleteInvoice(invoiceId: number): Observable<void> {
    return this.http.delete<void>(`${baseUrl}${this.baseEndpoint}/${invoiceId}`);
  }

  /**
   * 4. Get Invoice by ID
   * GET /invoices-v2/:invoiceId
   */
  getInvoiceById(invoiceId: number): Observable<any> {
    return this.http.get<any>(`${baseUrl}${this.baseEndpoint}/${invoiceId}`);
  }

  /**
   * 5. Get Invoice by UUID
   * GET /invoices-v2/uuid/:invoiceUuid
   */
  getInvoiceByUuid(invoiceUuid: string): Observable<any> {
    return this.http.get<any>(`${baseUrl}${this.baseEndpoint}/uuid/${invoiceUuid}`);
  }

  /**
   * 6. Confirm Invoice
   * GET /invoices-v2/confirm/:invoiceId
   */
  confirmInvoice(invoiceId: number): Observable<InvoiceResponseDto> {
    return this.http.get<InvoiceResponseDto>(`${baseUrl}${this.baseEndpoint}/confirm/${invoiceId}`);
  }

  /**
   * 6.1 Convert Invoice
   * POST /invoices-v2/:invoiceId/convert
   */
  convertInvoice(invoiceId: number): Observable<any> {
    return this.http.post<any>(`${baseUrl}${this.baseEndpoint}/${invoiceId}/convert`, {});
  }

  /**
   * Set allocation_number for an unconfirmed invoice (tax authority send on confirm).
   * PUT /invoices-v2/:invoiceId/allocation-number
   */
  updateAllocationNumber(
    invoiceId: number,
    allocation_number: 0 | 1,
  ): Observable<UpdateAllocationNumberResponse> {
    return this.http.put<UpdateAllocationNumberResponse>(
      `${baseUrl}${this.baseEndpoint}/${invoiceId}/allocation-number`,
      { allocation_number },
    );
  }

  /**
   * Merge multiple confirmed same-type same-customer invoices into one tax invoice.
   * POST /invoices-v2/bulk-convert
   */
  bulkConvertInvoices(invoice_ids: number[]): Observable<BulkConvertInvoicesResponse> {
    return this.http.post<BulkConvertInvoicesResponse>(
      `${baseUrl}${this.baseEndpoint}/bulk-convert`,
      { invoice_ids },
    );
  }

  /**
   * PATCH /invoices-v2/bulk/mark-merged — set is_merged on many invoices (or clear with false).
   */
  markInvoicesMerged(
    invoice_ids: number[],
    is_merged: boolean = true,
  ): Observable<MarkInvoicesMergedResponse> {
    return this.http.patch<MarkInvoicesMergedResponse>(
      `${baseUrl}${this.baseEndpoint}/bulk/mark-merged`,
      { invoice_ids, is_merged },
    );
  }

  /**
   * 7. Get Filtered Invoices
   * GET /invoices-v2/filter
   */
  getFilteredInvoices(filters: FilterInvoicesV2Dto): Observable<InvoiceListResponseDto> {
    const params = this.buildFilterParams(filters);
    return this.http.get<InvoiceListResponseDto>(`${baseUrl}${this.baseEndpoint}/filter`, { params });
  }

  /**
   * Export filtered invoices as PDF (same query params as /filter; limit/offset ignored server-side).
   * GET /invoices-v2/export/pdf
   */
  exportFilteredInvoicesPdf(
    filters: Partial<FilterInvoicesV2Dto>,
    lang: string,
  ): Observable<HttpResponse<Blob>> {
    const params = this.buildFilterParams(filters, { omitPagination: true }).set(
      'lang',
      this.normalizeExportLang(lang),
    );
    return this.http.get(`${baseUrl}${this.baseEndpoint}/export/pdf`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  /**
   * Export filtered invoices as Excel (.xlsx).
   * GET /invoices-v2/export/excel
   */
  exportFilteredInvoicesExcel(
    filters: Partial<FilterInvoicesV2Dto>,
    lang: string,
  ): Observable<HttpResponse<Blob>> {
    const params = this.buildFilterParams(filters, { omitPagination: true }).set(
      'lang',
      this.normalizeExportLang(lang),
    );
    return this.http.get(`${baseUrl}${this.baseEndpoint}/export/excel`, {
      params,
      responseType: 'blob',
      observe: 'response',
    });
  }

  private normalizeExportLang(lang: string): string {
    const l = (lang || 'ar').toLowerCase().slice(0, 2);
    return l === 'en' || l === 'he' || l === 'ar' ? l : 'ar';
  }

  /**
   * 8. Get Invoice Statistics
   * GET /invoices-v2/stats
   */
  getInvoiceStats(filters?: Omit<FilterInvoicesV2Dto, 'limit' | 'offset' | 'sort' | 'sortIndex'>): Observable<InvoiceStatsResponse> {
    const params = filters ? this.buildFilterParams(filters, { omitPagination: true }) : undefined;
    return this.http.get<InvoiceStatsResponse>(`${baseUrl}${this.baseEndpoint}/stats`, { params });
  }

  /**
   * 9. Generate PDF
   * GET /invoices-v2/:invoiceId/pdf?lang={lang}
   */
  generatePdf(invoiceId: number, lang?: string): Observable<{ pdf_link: string }> {
    const params = lang ? new HttpParams().set('lang', lang) : undefined;
    return this.http.get<{ pdf_link: string }>(`${baseUrl}${this.baseEndpoint}/${invoiceId}/pdf`, { params });
  }

  /**
   * Reprint copy PDF (distinct from original invoice PDF).
   * GET /invoices-v2/:invoiceId/pdf/reprint?lang={lang}
   * Response body is raw PDF bytes (not JSON like …/pdf).
   */
  getReprintPdf(invoiceId: number, lang?: string): Observable<Blob> {
    const params = lang ? new HttpParams().set('lang', lang) : undefined;
    return this.http.get(`${baseUrl}${this.baseEndpoint}/${invoiceId}/pdf/reprint`, {
      params,
      responseType: 'blob',
    });
  }

  /**
   * 10. Get Invoice HTML
   * GET /invoices-v2/:invoiceId/html?lang={lang}
   */
  getInvoiceHtml(invoiceId: number, lang?: string): Observable<string> {
    const params = lang ? new HttpParams().set('lang', lang) : undefined;
    return this.http.get(`${baseUrl}${this.baseEndpoint}/${invoiceId}/html`, {
      params,
      responseType: 'text',
    });
  }

  /**
   * 11. Create Reverse Invoice
   * POST /invoices-v2/:invoiceId/reverse
   */
  createReverseInvoice(invoiceId: number): Observable<ReverseInvoiceResponse> {
    return this.http.post<ReverseInvoiceResponse>(`${baseUrl}${this.baseEndpoint}/${invoiceId}/reverse`, {});
  }

  /**
   * 12. Predict Next Document Number
   * GET /invoices-v2/document-number/predict/:companyId/:invoiceType
   */
  predictDocumentNumber(companyId: number, invoiceType: number): Observable<DocumentNumberPredictResponse> {
    return this.http.get<DocumentNumberPredictResponse>(
      `${baseUrl}${this.baseEndpoint}/document-number/predict/${companyId}/${invoiceType}`
    );
  }

  /**
   * 13. Generate Document Number
   * GET /invoices-v2/document-number/generate/:companyId/:invoiceType
   */
  generateDocumentNumber(companyId: number, invoiceType: number): Observable<DocumentNumberGenerateResponse> {
    return this.http.get<DocumentNumberGenerateResponse>(
      `${baseUrl}${this.baseEndpoint}/document-number/generate/${companyId}/${invoiceType}`
    );
  }

  /**
   * 14. Get Document Number Statistics
   * GET /invoices-v2/document-number/stats/:companyId/:invoiceType
   */
  getDocumentNumberStats(companyId: number, invoiceType: number): Observable<DocumentNumberStatsResponse> {
    return this.http.get<DocumentNumberStatsResponse>(
      `${baseUrl}${this.baseEndpoint}/document-number/stats/${companyId}/${invoiceType}`
    );
  }

  /**
   * 15. Check Sequence Exists
   * GET /invoices-v2/document-number/check-sequence/:companyId/:invoiceType
   */
  checkSequenceExists(companyId: number, invoiceType: number): Observable<CheckSequenceResponse> {
    return this.http.get<CheckSequenceResponse>(
      `${baseUrl}${this.baseEndpoint}/document-number/check-sequence/${companyId}/${invoiceType}`
    );
  }

  /**
   * 16. Cancel Invoice (Tax Authority)
   * GET /invoices-v2/integration/cancel/:invoiceId
   */
  cancelInvoice(invoiceId: number): Observable<TaxAuthorityActionResponse> {
    return this.http.get<TaxAuthorityActionResponse>(
      `${baseUrl}${this.baseEndpoint}/integration/cancel/${invoiceId}`
    );
  }

  /**
   * 17. Continue Invoice (Tax Authority)
   * GET /invoices-v2/integration/continue/:invoiceId
   */
  continueInvoice(invoiceId: number): Observable<TaxAuthorityActionResponse> {
    return this.http.get<TaxAuthorityActionResponse>(
      `${baseUrl}${this.baseEndpoint}/integration/continue/${invoiceId}`
    );
  }

  /**
   * 18. Further Objection (Tax Authority)
   * GET /invoices-v2/integration/further-objection/:invoiceId
   */
  furtherObjection(invoiceId: number): Observable<TaxAuthorityActionResponse> {
    return this.http.get<TaxAuthorityActionResponse>(
      `${baseUrl}${this.baseEndpoint}/integration/further-objection/${invoiceId}`
    );
  }

  /**
   * 19. After Hearing Approval (Tax Authority)
   * GET /invoices-v2/integration/after-hearing-approval/:invoiceId
   */
  afterHearingApproval(invoiceId: number): Observable<TaxAuthorityActionResponse> {
    return this.http.get<TaxAuthorityActionResponse>(
      `${baseUrl}${this.baseEndpoint}/integration/after-hearing-approval/${invoiceId}`
    );
  }

  /**
   * 20. Resend confirmed invoice to tax authority (document_number set, no confirmation_number).
   * GET /invoices-v2/integration/send-second/:invoiceId
   */
  sendInvoiceSecond(invoiceId: number): Observable<TaxAuthorityActionResponse> {
    return this.http.get<TaxAuthorityActionResponse>(
      `${baseUrl}${this.baseEndpoint}/integration/send-second/${invoiceId}`
    );
  }

  /**
   * 21. Reverse the charge (cancel + reverse + zero-VAT reissue to tax authority).
   * GET /invoices-v2/integration/reverse-charge/:invoiceId
   */
  reverseCharge(invoiceId: number): Observable<TaxAuthorityActionResponse> {
    return this.http.get<TaxAuthorityActionResponse>(
      `${baseUrl}${this.baseEndpoint}/integration/reverse-charge/${invoiceId}`
    );
  }

  /**
   * 22. Export Multiple Invoices as Merged PDF
   * POST /export-pdf/multiple-invoices-pdf-merge?invoiceIds=1,2,3
   */
  exportMultipleInvoicesPdf(invoiceIds: number[]): Observable<Blob> {
    const params = new HttpParams().set('invoiceIds', invoiceIds.join(','));
    return this.http.post(`${baseUrl}export-pdf/multiple-invoices-pdf-merge`, null, {
      params,
      responseType: 'blob'
    });
  }

  /**
   * Get simple invoice list for selection (confirmed invoices only)
   */
  getInvoicesForSelection(companyId?: number, limit: number = 50): Observable<any> {
    let params = new HttpParams()
      .set('limit', limit.toString())
      .set('status', '1'); // Only confirmed invoices (status = 1)
    
    if (companyId) {
      params = params.set('companyId', companyId.toString());
    }
    
    return this.http.get<any>(`${baseUrl}${this.baseEndpoint}/filter`, { params });
  }

  /**
   * Helper method to format date to YYYY-MM-DD string
   */
  private formatDateForApi(date: any): string | null {
    if (!date) return null;
    
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }
    
    if (typeof date === 'string') {
      // If already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
        return date;
      }
      // Handle DD/MM/YYYY format
      if (date.includes('/')) {
        const parts = date.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[1]}-${parts[0]}`;
        }
      }
    }
    
    return String(date);
  }

  /**
   * Helper method to build filter query parameters
   */
  private buildFilterParams(
    filters: Partial<FilterInvoicesV2Dto>,
    options?: { omitPagination?: boolean },
  ): HttpParams {
    let params = new HttpParams();

    if (filters.fromDate) {
      const formattedDate = this.formatDateForApi(filters.fromDate);
      if (formattedDate) {
        params = params.set('fromDate', formattedDate);
      }
    }
    if (filters.toDate) {
      const formattedDate = this.formatDateForApi(filters.toDate);
      if (formattedDate) {
        params = params.set('toDate', formattedDate);
      }
    }
    if (filters.dueDateFrom) {
      const formattedDate = this.formatDateForApi(filters.dueDateFrom);
      if (formattedDate) {
        params = params.set('dueDateFrom', formattedDate);
      }
    }
    if (filters.dueDateTo) {
      const formattedDate = this.formatDateForApi(filters.dueDateTo);
      if (formattedDate) {
        params = params.set('dueDateTo', formattedDate);
      }
    }
    if (filters.has_due_date !== undefined && filters.has_due_date !== null) {
      params = params.set('has_due_date', String(filters.has_due_date));
    }
    if (filters.payment_status !== undefined && filters.payment_status !== null) {
      params = params.set('payment_status', String(filters.payment_status));
    }
    if (filters.payment_statuses?.trim()) {
      params = params.set('payment_statuses', filters.payment_statuses.trim());
    }
    if (filters.companyId !== undefined && filters.companyId !== null) {
      params = params.set('companyId', filters.companyId.toString());
    }
    if (filters.customer_id !== undefined && filters.customer_id !== null) {
      params = params.set('customer_id', filters.customer_id.toString());
    }
    if (filters.invoice_types?.length) {
      params = params.set('invoice_types', filters.invoice_types.join(','));
    }
    if (filters.invoice_type !== undefined && filters.invoice_type !== null) {
      params = params.set('invoice_type', filters.invoice_type.toString());
    }
    if (filters.status !== undefined && filters.status !== null) {
      params = params.set('status', filters.status.toString());
    }
    if (filters.confirm !== undefined) {
      params = params.set('confirm', filters.confirm.toString());
    }
    if (filters.is_deleted !== undefined) {
      params = params.set('is_deleted', filters.is_deleted.toString());
    }
    if (filters.searchKeyword) {
      params = params.set('searchKeyword', filters.searchKeyword);
    }
    if (filters.sort) {
      params = params.set('sort', filters.sort);
    }
    if (filters.sortIndex !== undefined && filters.sortIndex !== null) {
      params = params.set(
        'sortIndex',
        String(Math.min(11, Math.max(0, Number(filters.sortIndex)))),
      );
    }
    if (!options?.omitPagination) {
      if (filters.limit !== undefined) {
        params = params.set('limit', filters.limit.toString());
      }
      if (filters.offset !== undefined) {
        params = params.set('offset', filters.offset.toString());
      }
    }
    if (filters.minAmount !== undefined && filters.minAmount !== null) {
      params = params.set('minAmount', filters.minAmount.toString());
    }
    if (filters.maxAmount !== undefined && filters.maxAmount !== null) {
      params = params.set('maxAmount', filters.maxAmount.toString());
    }
    if (filters.document_number) {
      params = params.set('document_number', filters.document_number);
    }
    if (filters.confirmation_number) {
      params = params.set('confirmation_number', filters.confirmation_number);
    }
    if (filters.is_in_procedure_cycle !== undefined && filters.is_in_procedure_cycle !== null) {
      params = params.set('is_in_procedure_cycle', filters.is_in_procedure_cycle.toString());
    }
    if (filters.currency?.trim()) {
      params = params.set('currency', filters.currency.trim().toUpperCase());
    }
    if (filters.currencies?.trim()) {
      params = params.set('currencies', filters.currencies.trim().toUpperCase());
    }
    if (filters.invoice_ids?.length) {
      params = params.set('invoice_ids', filters.invoice_ids.join(','));
    }

    return params;
  }
}

